import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import { FEATURED_JOBS } from "@/mocks/jobs";

const URGENCY_COLORS: Record<string, string> = {
  URGENT: "bg-red-100 text-red-700",
  TODAY: "bg-red-50 text-red-600",
  TOMORROW: "bg-orange-100 text-orange-700",
  "THIS WEEK": "bg-yellow-100 text-yellow-700",
  FLEXIBLE: "bg-green-100 text-green-700",
};

type FormState = {
  name: string;
  email: string;
  phone: string;
  experience: string;
  message: string;
};

function ApplyForm({ jobTitle, jobId }: { jobTitle: string; jobId: number }) {
  const [form, setForm] = useState<FormState>({ name: "", email: "", phone: "", experience: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name === "message") setCharCount(value.length);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const data = new URLSearchParams();
    data.append("jobTitle", jobTitle);
    data.append("jobId", String(jobId));
    Object.entries(form).forEach(([k, v]) => data.append(k, v));
    try {
      await fetch("https://readdy.ai/api/form/d7644okbmgf2o8mm7170", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
    } finally {
      setLoading(false);
      setSubmitted(true);
    }
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
        <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-4">
          <i className="ri-check-double-line text-emerald-600 text-3xl" />
        </div>
        <h3 className="text-xl font-extrabold text-gray-900 mb-2">Application Sent!</h3>
        <p className="text-sm text-gray-500 mb-1">
          Your application for <span className="font-semibold text-gray-700">{jobTitle}</span> has been received.
        </p>
        <p className="text-sm text-gray-400 mb-6">
          We're generating your AI-tailored resume and cover letter now. You'll hear back within minutes.
        </p>
        <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3 text-sm text-emerald-800 font-medium">
          <i className="ri-robot-line mr-2" />
          AI resume generation in progress...
        </div>
      </div>
    );
  }

  return (
    <form
      data-readdy-form
      onSubmit={handleSubmit}
      className="bg-white rounded-2xl border border-gray-200 p-6 flex flex-col gap-4"
    >
      <div>
        <h3 className="text-lg font-extrabold text-gray-900 mb-0.5">Apply Now</h3>
        <p className="text-xs text-gray-400">Takes under 2 minutes. AI generates your resume automatically.</p>
      </div>

      <div>
        <label className="block text-xs font-bold text-gray-700 mb-1.5">Full Name</label>
        <input
          name="name"
          type="text"
          required
          value={form.name}
          onChange={handleChange}
          placeholder="Jane Doe"
          className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-400 transition"
        />
      </div>

      <div>
        <label className="block text-xs font-bold text-gray-700 mb-1.5">Email Address</label>
        <input
          name="email"
          type="email"
          required
          value={form.email}
          onChange={handleChange}
          placeholder="you@email.com"
          className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-400 transition"
        />
      </div>

      <div>
        <label className="block text-xs font-bold text-gray-700 mb-1.5">Phone Number</label>
        <input
          name="phone"
          type="tel"
          required
          value={form.phone}
          onChange={handleChange}
          placeholder="(512) 555-0100"
          className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-400 transition"
        />
      </div>

      <div>
        <label className="block text-xs font-bold text-gray-700 mb-1.5">Years of Experience</label>
        <select
          name="experience"
          required
          value={form.experience}
          onChange={handleChange}
          className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-400 transition bg-white cursor-pointer"
        >
          <option value="">Select experience level</option>
          <option value="none">No experience — willing to learn</option>
          <option value="less1">Less than 1 year</option>
          <option value="1-2">1–2 years</option>
          <option value="3-5">3–5 years</option>
          <option value="5+">5+ years</option>
        </select>
      </div>

      <div>
        <label className="block text-xs font-bold text-gray-700 mb-1.5">
          Why should we pick you?
          <span className="ml-auto float-right font-normal text-gray-400">{charCount}/500</span>
        </label>
        <textarea
          name="message"
          required
          maxLength={500}
          rows={4}
          value={form.message}
          onChange={handleChange}
          placeholder="Tell us a bit about yourself, relevant experience, or why you're a great fit..."
          className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-400 transition resize-none"
        />
        {charCount > 480 && (
          <p className="text-xs text-red-500 mt-1">{500 - charCount} characters remaining</p>
        )}
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full py-3.5 bg-emerald-500 text-white font-bold rounded-xl text-sm hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
      >
        {loading ? (
          <><i className="ri-loader-4-line animate-spin mr-2" />Submitting...</>
        ) : (
          <><i className="ri-send-plane-line mr-2" />Submit Application</>
        )}
      </button>

      <p className="text-xs text-center text-gray-400">
        <i className="ri-robot-line mr-1" />
        AI will craft a tailored resume &amp; cover letter for you
      </p>
    </form>
  );
}

export default function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const job = FEATURED_JOBS.find((j) => j.id === Number(id));

  if (!job) {
    return (
      <div className="min-h-screen bg-gray-50">
        <SiteHeader />
        <div className="max-w-2xl mx-auto px-4 py-32 text-center">
          <i className="ri-error-warning-line text-5xl text-gray-300 mb-4 block" />
          <h1 className="text-2xl font-extrabold text-gray-900 mb-2">Job Not Found</h1>
          <p className="text-gray-500 mb-8">This listing may have been filled or removed.</p>
          <button
            onClick={() => navigate("/find-work")}
            className="px-6 py-3 bg-emerald-500 text-white font-bold rounded-xl text-sm hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line mr-2" />Browse All Jobs
          </button>
        </div>
      </div>
    );
  }

  const urgencyClass = URGENCY_COLORS[job.urgency] ?? "bg-gray-100 text-gray-600";

  return (
    <div className="min-h-screen bg-[#f8f8f6]">
      <SiteHeader />

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-400 mb-8">
          <Link to="/" className="hover:text-gray-600 transition-colors cursor-pointer">Home</Link>
          <i className="ri-arrow-right-s-line" />
          <Link to="/find-work" className="hover:text-gray-600 transition-colors cursor-pointer">Find Work</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-gray-600 font-medium truncate max-w-xs">{job.title}</span>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 items-start">
          {/* ── LEFT COLUMN ── */}
          <div className="flex-1 min-w-0 flex flex-col gap-6">

            {/* Job Header Card */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8">
              <div className="flex items-start justify-between gap-4 mb-5">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-gray-50 border border-gray-100 shrink-0">
                    <i className={`${job.icon} text-2xl text-gray-600`} />
                  </div>
                  <div>
                    <h1 className="text-2xl font-extrabold text-gray-900 leading-tight">{job.title}</h1>
                    <p className="text-gray-500 text-sm mt-0.5">{job.company}</p>
                  </div>
                </div>
                <span className={`text-xs font-bold px-3 py-1.5 rounded-full whitespace-nowrap ${urgencyClass}`}>
                  {job.urgency}
                </span>
              </div>

              {/* Stats Row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                <div className="bg-gray-50 rounded-xl p-3.5">
                  <div className="text-xs text-gray-400 mb-0.5">Pay Rate</div>
                  <div className="text-lg font-extrabold text-gray-900">{job.pay}</div>
                </div>
                <div className="bg-gray-50 rounded-xl p-3.5">
                  <div className="text-xs text-gray-400 mb-0.5">Distance</div>
                  <div className="text-lg font-extrabold text-gray-900">{job.distance}</div>
                </div>
                <div className="bg-gray-50 rounded-xl p-3.5">
                  <div className="text-xs text-gray-400 mb-0.5">Open Slots</div>
                  <div className="text-lg font-extrabold text-gray-900">{job.slots}</div>
                </div>
                <div className="bg-gray-50 rounded-xl p-3.5">
                  <div className="text-xs text-gray-400 mb-0.5">AI Match</div>
                  <div className="text-lg font-extrabold text-emerald-600">{job.match}%</div>
                </div>
              </div>

              {/* Meta */}
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-500 border-t border-gray-100 pt-4">
                <span className="flex items-center gap-1.5">
                  <i className="ri-map-pin-line text-gray-400" />{job.location}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line text-gray-400" />{job.schedule}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line text-gray-400" />Starts: {job.startDate}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-history-line text-gray-400" />Posted {job.posted}
                </span>
              </div>
            </div>

            {/* Full Description */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8">
              <h2 className="text-base font-extrabold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-file-text-line text-emerald-500" />
                Job Description
              </h2>
              <p className="text-sm text-gray-600 leading-relaxed">{job.fullDescription}</p>
            </div>

            {/* Requirements */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8">
              <h2 className="text-base font-extrabold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-checkbox-circle-line text-emerald-500" />
                Requirements
              </h2>
              <ul className="flex flex-col gap-3">
                {job.requirements.map((req, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-gray-600">
                    <div className="w-5 h-5 flex items-center justify-center rounded-full bg-emerald-50 border border-emerald-100 shrink-0 mt-0.5">
                      <i className="ri-check-line text-emerald-600 text-xs" />
                    </div>
                    {req}
                  </li>
                ))}
              </ul>
            </div>

            {/* Skills / Capabilities */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8">
              <h2 className="text-base font-extrabold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-tools-line text-emerald-500" />
                Skills &amp; Capabilities
              </h2>
              <div className="flex flex-wrap gap-2">
                {job.capabilities.map((cap) => (
                  <span key={cap} className="text-sm bg-gray-50 border border-gray-200 text-gray-700 px-3.5 py-1.5 rounded-xl font-medium">
                    {cap}
                  </span>
                ))}
              </div>
            </div>

            {/* Perks */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8">
              <h2 className="text-base font-extrabold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-gift-line text-emerald-500" />
                Perks &amp; Benefits
              </h2>
              <ul className="flex flex-col gap-3">
                {job.perks.map((perk, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-gray-600">
                    <div className="w-5 h-5 flex items-center justify-center rounded-full bg-amber-50 border border-amber-100 shrink-0 mt-0.5">
                      <i className="ri-star-line text-amber-500 text-xs" />
                    </div>
                    {perk}
                  </li>
                ))}
              </ul>
            </div>

            {/* About Company */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8">
              <h2 className="text-base font-extrabold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-building-line text-emerald-500" />
                About {job.company}
              </h2>
              <p className="text-sm text-gray-600 leading-relaxed">{job.companyAbout}</p>
            </div>

            {/* Similar Jobs hint */}
            <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-5 flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-gray-700">Looking for more options?</p>
                <p className="text-xs text-gray-400 mt-0.5">Browse hundreds of listings in {job.category}</p>
              </div>
              <Link
                to="/find-work"
                className="px-5 py-2.5 border border-gray-300 text-gray-600 text-xs font-bold rounded-xl hover:border-emerald-400 hover:text-emerald-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Browse All Jobs <i className="ri-arrow-right-line ml-1" />
              </Link>
            </div>
          </div>

          {/* ── RIGHT COLUMN — Sticky Apply ── */}
          <div className="w-full lg:w-[380px] shrink-0">
            <div className="lg:sticky lg:top-24 flex flex-col gap-4">
              {/* Match Badge */}
              <div className="bg-emerald-50 border border-emerald-100 rounded-2xl px-5 py-4 flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-emerald-500 rounded-xl shrink-0">
                  <i className="ri-bar-chart-fill text-white text-lg" />
                </div>
                <div>
                  <div className="text-sm font-extrabold text-emerald-800">{job.match}% Capability Match</div>
                  <div className="text-xs text-emerald-600 mt-0.5">Your profile fits this job well</div>
                </div>
              </div>

              {/* Apply Form */}
              <ApplyForm jobTitle={job.title} jobId={job.id} />

              {/* Quick Stats */}
              <div className="bg-white rounded-2xl border border-gray-200 p-5">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Quick Stats</p>
                <div className="flex flex-col gap-2.5">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Category</span>
                    <span className="font-semibold text-gray-800">{job.category}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Open Slots</span>
                    <span className="font-semibold text-gray-800">{job.slots} remaining</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Starts</span>
                    <span className="font-semibold text-gray-800">{job.startDate}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Schedule</span>
                    <span className="font-semibold text-gray-800">{job.schedule}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Posted</span>
                    <span className="font-semibold text-gray-800">{job.posted}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
